import {computed} from "vue";
import { useRoute } from "vue-router";
import mdKatex from "@traptitech/markdown-it-katex";
import MarkdownIt from "markdown-it";
import mila from 'markdown-it-link-attributes';
import mic from 'markdown-it-container';
import hljs from "highlight.js";
import 'highlight.js/styles/github.css';
import {i18n} from "/@common/i18n";

const route = useRoute();
let chatMode = computed(() => route.path.indexOf('chat') != -1);

const hotIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="50" height="50"><defs><linearGradient id="a" y2="100%" x2="50%" y1="0%" x1="50%"><stop offset="0%" stop-color="#FF9DAB"></stop><stop offset="100%" stop-color="#F54B5B"></stop></linearGradient></defs><g fill-rule="evenodd" fill="none"><path d="M0 0h18v18H0z"></path><path fill-rule="nonzero" fill="url(#a)" d="M12.269 4.77c.279 1.115-.249 2.404-1.241 3.008-.001-1.76-.189-3.43-1.22-4.829C9.24 2.184 8.37 1.52 7.523 1c.066 1.4.056 4.068-1.629 5.49C4.21 7.913 3 9.254 3 11.632c0 3.955 3.123 5.05 5.92 5.118 2.797.068 6.08-1.905 6.08-5.532 0-3.626-1.11-5.198-2.731-6.449Z"></path></g></svg>';

/**
 * @description 公共的markdown解析方法
 */
export const addLoadEventToImgTags = (htmlString: string) => {
    const regex = /(<img\s+[^>]*)(>)/g;
    const replacement = `$1  onclick="showPreviewImg(event)" $2`;
    return htmlString.replace(regex, replacement);
};
// 定义一个函数，接受一个对象作为参数，返回一个字符串
export const textToMarkdown = (text: string, citations: any) => {
    // 判断json格式
    try {
        const jsonObj = JSON.parse(text); // 将字符串解析为一个对象
        const jsonFormatted = JSON.stringify(jsonObj, null, 2);
        text = '```json\n' + jsonFormatted + '\n```';
    } catch (error) {}

    // 截取:::r 结束的字符串
    if (text.endsWith('\n:')) {
        text = text.slice(0, -3);
    }
    if (text.endsWith('\n::')) {
        text = text.slice(0, -4);
    }
    if (text.endsWith('\n:::')) {
        text = text.slice(0, -5);
    }
    if (text.endsWith('\n:::r')) {
        text = text.slice(0, -6);
    }
    if (text.endsWith('\n:::r ')) {
        text = text.slice(0, -7);
    }

    // 初始化一个空字符串，用于存储结果
    let result = '';
    if (!citations) {
        return {
            result: text,
            isMarkdownImg: []
        };
    }
    // 是否包含图片
    const regexImage = /!\[.*?\]\(.*?\)/g;
    const isMarkdownImg = text.match(regexImage) || [];

    // 遍历对象的 res_info 属性，获取每个键值对
    for (const key in citations) {
        // 获取当前键值对的值，即一个子对象
        const value = citations[key];
        const { no, fileUrl = '#', fileName } = value;
        // 将子对象的 title 和 context 属性拼接成 markdown 的链接引用格式，并添加到结果字符串中
        if (route.path.indexOf('chat') != -1) {
            result += `[${no}]: ${fileUrl ? encodeURI(fileUrl) : null} \'${fileName}\'\n\n`;
        } else {
            result += `[${no}]: ${fileUrl ? null : null} \'${fileName}\'\n\n`;
        }
    }
    // 在结果字符串末尾添加一个空行
    result += '\n';
    // 将对象的 res 属性的值，即一个字符串，替换其中的数字为上标引用，并在每个数字后面加上一个空格，并添加到结果字符串中
    result += text.replace(/\[(\d+)\]/g, (match, p1) => {
        // 使用回调函数替换匹配的内容
        const num = Number(p1); // 将 $1 转换为数字
        return `[${num}][${num}]`; // 返回对应的 context
    });
    // 返回结果字符串
    return {
        result,
        isMarkdownImg
    };
};

export const initTextMarkdown = (params?: any) => {
    const highlightBlock = (str: string, lang?: string) => {
        let langStr = '';
        if (lang) {
            langStr = `<span class="code-block-header__lang">${lang}</span>`;
        }
        return `<pre class="code-block-wrapper"><div class="code-block-header">${langStr}<span class="code-block-header__copy" onclick="copyBtn()">${i18n.global.t('chat.center.copyCode')}</span><span class="code-block-header__theme" onclick="themeBtn()">${i18n.global.t('chat.center.themeChange')}</span></div><code class="hljs code-block-body ${lang}">${str}</code></pre>`;
    }

    const nullLinkPlugin = (md) => {
        const defaultRender = md.renderer.rules.link_open || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

        md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
            const token = tokens[idx];
            const hrefIndex = token.attrIndex('href');
            const targetIndex = token.attrIndex('target');
            // const hrefIndex = token.attrIndex('href');

            if (hrefIndex !== -1 && token.attrs[hrefIndex][1] === 'null') {
                // 将链接地址为 null 的 href 替换为 javascript:void(0)
                token.attrs[hrefIndex][1] = 'javascript:void(0)';
                token.attrs[targetIndex][1] = '';
            }

            return defaultRender(tokens, idx, options, env, self);
        };
    };

    const mdi = new MarkdownIt({
        html: true,
        linkify: false,
        highlight(code, language) {
            const validLang = !!(language && hljs.getLanguage(language));
            if (validLang) {
                const lang = language ?? '';
                return highlightBlock(hljs.highlight(code, { language: lang }).value, lang);
            }
            return highlightBlock(hljs.highlightAuto(code).value, '');
        },
    });
    mdi.use(mdKatex, { blockClass: 'katexmath-block rounded-md p-[10px]', errorColor: ' #cc0000' });
    // 注册插件，并指定自定义容器的名称和渲染方式
    mdi.use(mic, 'r', {
        // 验证自定义容器是否符合规则
        validate: function (params) {
            return params.trim().match(/^r\s+(.*)$/);
        },
        // 渲染自定义容器的开头
        render: function (tokens, idx) {
            const recommendKey = [
                { name: '热点摘要', key: '内容进行概述', type: 1, param: {} },
                { name: '观点分析', key: '各方观点', type: 2, param: { get_news_num: 1 } },
            ];
            const m = tokens[idx].info.trim().match(/^r\s+(.*)$/);
            if (tokens[idx].nesting === 1) {
                const arr = m[1].split(',');
                let result = '';
                // 生成一个 10 到 999 之间的随机整数
                const random = Math.floor(Math.random() * (999 - 10 + 1)) + 10;
                if (arr[1]) {
                    result = '<span>相关：</span>';
                    recommendKey.forEach((element, index) => {
                        const text = arr[0].split('.')[1];
                        const key = element.type === 1 ? `请对${text} 的${element.key}，详细列举：` : `关于${text} 的${element.key}，详细列举：`;
                        result += `
						<button type="button"  onclick='setsendMessage("${key}",${JSON.stringify(element.param)},${
                            params
                        })'  class="w-btn w-btn-outline recommend-item recommend-item-${index}">
							${element.name}
						</button>
					`;
                    });
                }
                if (!chatMode.value) {
                    const key = arr[0];
                    const params = {};
                    return `
					<div  class="recommend-title target-${idx * random} recommend-title-pointer" onclick='setsendMessage("${key}",${JSON.stringify(params)},${
                        params
                    })'>
					${arr[0]}
					</div>
				`;
                } else {
                    const hotStr = arr[1] ? `<xy-button  class="recommend-hot">${hotIcon}${arr[1]}</xy-button>` : ``;
                    // 返回一个带有标题和隐藏按钮的div元素
                    return `
					<div  class="recommend-title target-${idx * random}">
					${arr[0]}
					${hotStr}
					</div>
					<div class="recommend-popup" style="margin-bottom:3px"  target=".target-${idx * random}" type="custom" >
					
					${result}
					</div>
					`;
                }
            } else {
                return '';
            }
        },
    });
    // 自定义插件
    mdi.use(nullLinkPlugin);
    mdi.use(mila, { attrs: { target: '_blank', rel: 'noopener' } });
    return mdi;
}

export const initMessageMarkdown = () => {
    const highlightBlock = (str: string, lang?: string) => {
        return `<pre class="code-block-wrapper"><code class="hljs code-block-body ${lang}">${str}</code></pre>`;
    }
    const mdi = new MarkdownIt({
        html: true,
        linkify: false,
        highlight(code, language) {
            const validLang = !!(language && hljs.getLanguage(language));
            if (validLang) {
                const lang = language ?? '';
                return highlightBlock(hljs.highlight(code, { language: lang }).value, lang);
            }
            return highlightBlock(hljs.highlightAuto(code).value, '');
        },
    });
    mdi.use(mdKatex, { blockClass: 'katexmath-block rounded-md p-[10px]', errorColor: ' #cc0000' });
    mdi.use(mic, 'r', {
        // 验证自定义容器是否符合规则
        validate: function (params) {
            return params.trim().match(/^r\s+(.*)$/);
        },
        // 渲染自定义容器的开头
        render: function (tokens, idx) {},
    });
    return mdi;
}